/**
 * ImportFromURLPage Helpers
 * Helper functions and utilities for URL import functionality
 */

import { imageUploadService } from '../services/imageUploadService';
import { recipeService } from '../services/recipeService';

/**
 * Validates a URL string
 */
export const validateUrl = (url: string): string | null => {
  if (!url.trim()) {
    return 'Please enter a recipe URL';
  }
  
  try {
    new URL(url);
    return null;
  } catch {
    return 'Please enter a valid URL';
  }
};

/**
 * Processes a parsed recipe with timeout and debugging
 */
export const processParsedRecipe = async (
  parsedRecipe: any, 
  debugLog?: (msg: string) => void
) => {
  const log = debugLog || ((msg: string) => console.log(`[ProcessRecipe] ${msg}`));
  
  log('Starting recipe processing');
  
  if (!parsedRecipe.title || !parsedRecipe.ingredients?.length) {
    throw new Error('Could not extract recipe information from this URL. Try copying and pasting the recipe text instead.');
  }

  log(`Processing recipe: ${parsedRecipe.title} with ${parsedRecipe.ingredients.length} ingredients`);

  // If there's an external image URL, try to upload it but don't let it block recipe creation
  let finalImageUrl = parsedRecipe.image_url;
  if (finalImageUrl && (finalImageUrl.startsWith('http://') || finalImageUrl.startsWith('https://'))) {
    log(`Attempting to upload image from: ${finalImageUrl.substring(0, 50)}...`);
    
    // Create a shorter timeout for image upload - don't let it block recipe creation
    const imageUploadTimeout = new Promise<string>((_, reject) => {
      setTimeout(() => reject(new Error('Image upload timeout after 5 seconds')), 5000);
    });
    
    try {
      // Race between image upload and timeout
      finalImageUrl = await Promise.race([
        imageUploadService.uploadImageFromUrl(
          parsedRecipe.image_url,
          parsedRecipe.title
        ),
        imageUploadTimeout
      ]) as string;
      log('Image uploaded successfully');
    } catch (error) {
      log(`Image upload skipped: ${(error as Error).message}. Using original URL`);
      // finalImageUrl remains as the original external URL as fallback
      // Don't throw - just use the original URL
    }
  }

  log('Creating recipe in database');
  
  // Add timeout to recipe creation
  const createRecipeTimeout = new Promise<never>((_, reject) => {
    setTimeout(() => reject(new Error('Recipe creation timeout after 15 seconds')), 15000);
  });
  
  try {
    const recipeData = {
      title: parsedRecipe.title!,
      description: parsedRecipe.description || '',
      ingredients: parsedRecipe.ingredients || [],
      instructions: parsedRecipe.instructions || [],
      prep_time: parsedRecipe.prep_time || 0,
      cook_time: parsedRecipe.cook_time || 0,
      servings: parsedRecipe.servings || 4,
      difficulty: parsedRecipe.difficulty || 'medium',
      tags: parsedRecipe.tags || [],
      image_url: finalImageUrl || undefined,
      // Include nutrition data from parsed recipe
      calories: parsedRecipe.calories,
      total_fat: parsedRecipe.total_fat,
      saturated_fat: parsedRecipe.saturated_fat,
      protein: parsedRecipe.protein,
      carbohydrates: parsedRecipe.carbohydrates,
      fiber: parsedRecipe.fiber,
      sugar: parsedRecipe.sugar,
      sodium: parsedRecipe.sodium,
    };
    
    log(`Saving recipe with data: ${JSON.stringify({ title: recipeData.title, ingredientCount: recipeData.ingredients.length })}`);
    
    const newRecipe = await Promise.race([
      recipeService.createRecipe(recipeData),
      createRecipeTimeout
    ]);
    
    log('Recipe created successfully');
    return newRecipe;
  } catch (error) {
    log(`Recipe creation failed: ${(error as Error).message}`);
    throw error;
  }
};

/**
 * Common supported sites for recipe import
 */
export const SUPPORTED_SITES = [
  { name: 'AllRecipes', icon: '🍳' },
  { name: 'Food Network', icon: '📺' },
  { name: 'Bon Appétit', icon: '🥘' },
  { name: 'Serious Eats', icon: '🔬' },
  { name: 'Simply Recipes', icon: '🏠' },
  { name: 'NYT Cooking', icon: '📰' },
  { name: 'BBC Good Food', icon: '🇬🇧' },
  { name: 'Epicurious', icon: '🍴' },
  { name: 'Tasty', icon: '🎬' },
  { name: 'Pinterest', icon: '📌' },
  { name: 'Food52', icon: '🏪' },
  { name: 'King Arthur Baking', icon: '🍞' },
  { name: 'Smitten Kitchen', icon: '🏙️' },
  { name: 'Cookie and Kate', icon: '🌱' },
  { name: 'Minimalist Baker', icon: '🧁' },
  { name: 'Budget Bytes', icon: '💰' },
  { name: 'Damn Delicious', icon: '😋' },
  { name: 'Half Baked Harvest', icon: '🏔️' },
  { name: 'Love and Lemons', icon: '🍋' },
  { name: 'Sally\'s Baking', icon: '🧑‍🍳' }
];

/**
 * Get error message for import failures
 */
export const getImportErrorMessage = (error: Error): string => {
  if (error.message.includes('timeout')) {
    return 'The import is taking too long. The site might be slow or blocking our access. Try copying and pasting the recipe instead.';
  }
  if (error.message.includes('Could not extract')) {
    return error.message;
  }
  return `Failed to import recipe: ${error.message}`;
};